import { FC } from 'react';
import Container from '@amzn/awsui-components-core/container';
import Header from '@amzn/awsui-components-core/header';
import FormField from '@amzn/awsui-components-core/form-field';
import Input from '@amzn/awsui-components-core/input';
import { useTranslate } from 'src/hooks/useTranslate';
import { SectionBaseProps } from '../../types';

export interface CampaignDetailsSectionProps extends SectionBaseProps {
    /** Current campaign title value */
    title: string;
    /** Callback to update the campaign title */
    setTitle: (value: string) => void;
    /** Inline validation error message for the title field */
    errorText?: string;
}

/**
 * Campaign details section — renders the campaign title input field.
 * Owns its own i18n bundle (`organisms.campaignDetailsSection`) so translations
 * are local to this component rather than prop-drilled from the page.
 */
const CampaignDetailsSection: FC<CampaignDetailsSectionProps> = ({
    title,
    setTitle,
    mode,
    disabled,
    errorText,
}) => {
    const { translate } = useTranslate('organisms.campaignDetailsSection');
    // Title is read-only after creation — disabled in both view and modify modes.
    const isDisabled = disabled ?? mode !== 'create';

    return (
        <Container header={<Header variant="h2">{translate('section_title')}</Header>}>
            <FormField
                label={translate('campaign_title_label')}
                errorText={errorText}
                i18nStrings={{ errorIconAriaLabel: translate('error_icon_aria_label') }}
            >
                <Input
                    value={title}
                    onChange={({ detail }) => setTitle(detail.value)}
                    placeholder={translate('campaign_title_placeholder')}
                    ariaRequired={true}
                    disabled={isDisabled}
                    data-testid="campaign-title"
                    invalid={!!errorText}
                />
            </FormField>
        </Container>
    );
};

export default CampaignDetailsSection;
